import React, { useState, useCallback } from "react";
import ReactDom from "react-dom";
import styles from "./filterConfigModal.module.css";
import CloseIcon from "../../../assets/icons/circle-xmark.svg";
import AddCircleIcon from "../../../assets/icons/add-circle.svg";
import api from "../../../http/api";

const FilterConfigModal = ({ onClose, onApply, filterFields, filterOperators, institutionId, filterOptionsEndpoint = "api/v1/FinanceInstitution/FilterOptions" }) => {
  const [filterRows, setFilterRows] = useState([
    { id: 1, field: "", operator: "eq", value: "", values: [], condition: "AND" }
  ]);
  const [rowFilterValues, setRowFilterValues] = useState({}); // {rowId: {values: [], loading: false}}

  const fetchFilterValues = useCallback(async (rowId, fieldName) => {
    if (!institutionId || !fieldName) return;
    
    try {
      setRowFilterValues(prev => ({
        ...prev,
        [rowId]: { values: prev[rowId]?.values || [], loading: true }
      }));
      
      const fieldMapping = filterFields.find(field => field.value === fieldName);
      const apiFieldName = fieldMapping ? fieldMapping.apiField : fieldName;
      
      const response = await api.client.get(
        filterOptionsEndpoint,
        {
          params: {
            fieldName: apiFieldName
          },
          headers: {
            institutionId: institutionId
          }
        }
      );
      
      setRowFilterValues(prev => ({
        ...prev,
        [rowId]: { values: response.data?.options || [], loading: false }
      }));
    } catch (error) {
      console.error("Erro ao buscar valores do filtro:", error);
      setRowFilterValues(prev => ({
        ...prev,
        [rowId]: { values: [], loading: false }
      }));
    }
  }, [institutionId, filterFields, filterOptionsEndpoint]);

  const handleAddRow = () => {
    setFilterRows([...filterRows, { id: Date.now(), field: "", operator: "eq", value: "", values: [], condition: "AND" }]);
  };

  const handleRowChange = (id, key, value) => {
    if (key === 'field') {
      // Quando o campo muda, resetar operador e valor, e buscar novos valores
      setFilterRows(filterRows.map(row => 
        row.id === id ? { ...row, field: value, operator: "eq", value: "", values: [] } : row
      ));
      if (value) {
        fetchFilterValues(id, value);
      } else {
        setRowFilterValues(prev => ({
          ...prev,
          [id]: { values: [], loading: false }
        }));
      }
    } else if (key === 'operator') {
      // Quando o operador muda, resetar valor
      setFilterRows(filterRows.map(row => 
        row.id === id ? { ...row, [key]: value, value: "", values: [] } : row
      ));
    } else if (key === 'values') {
      // Para operador 'in', value é um array
      setFilterRows(filterRows.map(row => 
        row.id === id ? { ...row, values: value } : row
      ));
    } else {
      setFilterRows(filterRows.map(row => 
        row.id === id ? { ...row, [key]: value } : row
      ));
    }
  };

  const handleCheckboxChange = (rowId, value, isChecked) => {
    const row = filterRows.find(r => r.id === rowId);
    if (!row) return;
    
    let newValues;
    if (isChecked) {
      newValues = [...row.values, value];
    } else {
      newValues = row.values.filter(v => v !== value);
    }
    
    handleRowChange(rowId, 'values', newValues);
  };

  const handleConditionChange = (id, condition) => {
    setFilterRows(filterRows.map(row => 
      row.id === id ? { ...row, condition } : row
    ));
  };

  // Verificar se a última linha está completamente preenchida
  const isLastRowComplete = () => {
    const lastRow = filterRows[filterRows.length - 1];
    if (!lastRow.field || !lastRow.operator) return false;
    
    if (lastRow.operator === 'in') {
      return lastRow.values && lastRow.values.length > 0;
    } else {
      return lastRow.value && lastRow.value.trim();
    }
  };

  // Verificar se há pelo menos um filtro válido
  const hasValidFilters = () => {
    return filterRows.some(row => {
      if (!row.field || !row.operator) return false;
      
      if (row.operator === 'in') {
        return row.values && row.values.length > 0;
      } else {
        return row.value && row.value.trim();
      }
    });
  };

  const handleApply = () => {
    // Filtrar apenas linhas com campos preenchidos
    const validFilters = filterRows
      .filter(row => {
        if (!row.field || !row.operator) return false;
        
        if (row.operator === 'in') {
          return row.values && row.values.length > 0;
        } else {
          return row.value && row.value.trim();
        }
      })
      .map((row, index) => ({
        field: row.field,
        operator: row.operator,
        value: row.operator === 'in' ? row.values.join(', ') : row.value,
        values: row.operator === 'in' ? row.values : [row.value],
        condition: index > 0 ? row.condition : null
      }));
    onApply(validFilters);
    onClose();
  };

  const modalContent = (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Cabeçalho */}
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Configurações de Exibição</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <img src={CloseIcon} alt="Fechar" />
          </button>
        </div>

        {/* Corpo do Modal */}
        <div className={styles.modalBody}>
          <h3 className={styles.sectionTitle}>Filtrar por Campo</h3>

          {filterRows.map((row, index) => (
            <React.Fragment key={row.id}>
              {/* Botões "e" / "ou" entre as linhas */}
              {index > 0 && (
                <div className={styles.conditionToggle}>
                  <div className={styles.conditionButtons}>
                    <button
                      type="button"
                      className={`${styles.conditionBtn} ${row.condition === 'AND' ? styles.conditionBtnActive : ''}`}
                      onClick={() => handleConditionChange(row.id, 'AND')}
                    >
                      e
                    </button>
                    <button
                      type="button"
                      className={`${styles.conditionBtn} ${row.condition === 'OR' ? styles.conditionBtnActive : ''}`}
                      onClick={() => handleConditionChange(row.id, 'OR')}
                    >
                      ou
                    </button>
                  </div>
                </div>
              )}

              <div className={styles.filterRow}>
                <div className={styles.filterField}>
                  <label>Se</label>
                  <select
                    value={row.field}
                    onChange={(e) => handleRowChange(row.id, 'field', e.target.value)}
                    className={styles.select}
                  >
                    <option value="">Selecione um campo</option>
                    {filterFields.map(field => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.filterField}>
                  <label>Condição</label>
                  <select
                    value={row.operator}
                    onChange={(e) => handleRowChange(row.id, 'operator', e.target.value)}
                    className={styles.select}
                  >
                    <option value="">Selecione uma condição</option>
                    {filterOperators.map(op => (
                      <option key={op.value} value={op.value}>
                        {op.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.filterField}>
                  <label>Atributo</label>
                  {['gt', 'lt', 'gte', 'lte'].includes(row.operator) ? (
                    <input
                      type="text"
                      value={row.value}
                      onChange={(e) => handleRowChange(row.id, 'value', e.target.value)}
                      className={styles.input}
                      placeholder="Digite o valor"
                    />
                  ) : row.operator === 'in' ? (
                    <div className={styles.checkboxContainer}>
                      {rowFilterValues[row.id]?.loading && (
                        <div className={styles.loadingMessage}>Carregando...</div>
                      )}
                      {!row.field && !rowFilterValues[row.id]?.loading && (
                        <div className={styles.emptyMessage}>Selecione um campo primeiro</div>
                      )}
                      {row.field && !rowFilterValues[row.id]?.loading && rowFilterValues[row.id]?.values?.length === 0 && (
                        <div className={styles.emptyMessage}>Nenhum valor disponível</div>
                      )}
                      {rowFilterValues[row.id]?.values?.map((value, idx) => (
                        <label key={idx} className={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            checked={row.values.includes(value)}
                            onChange={(e) => handleCheckboxChange(row.id, value, e.target.checked)}
                            className={styles.checkbox}
                          />
                          <span className={styles.checkboxText}>{value}</span>
                        </label>
                      ))}
                      {row.values && row.values.length > 0 && (
                        <div className={styles.selectedValuesInfo}>
                          {row.values.length} valor(es) selecionado(s)
                        </div>
                      )}
                    </div>
                  ) : (
                    <select
                      value={row.value}
                      onChange={(e) => handleRowChange(row.id, 'value', e.target.value)}
                      className={styles.select}
                      disabled={!row.field || rowFilterValues[row.id]?.loading}
                    >
                      <option value="">
                        {rowFilterValues[row.id]?.loading 
                          ? "Carregando..." 
                          : !row.field 
                          ? "Selecione um campo primeiro"
                          : "Selecione um valor"}
                      </option>
                      {rowFilterValues[row.id]?.values?.map((value, idx) => (
                        <option key={idx} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </React.Fragment>
          ))}

          <button 
            className={styles.addPropertyButton} 
            onClick={handleAddRow}
            disabled={!isLastRowComplete()}
          >
            <img src={AddCircleIcon} alt="Adicionar" />
            Adicionar propriedade
          </button>
        </div>

        {/* Rodapé */}
        <div className={styles.modalFooter}>
          <button className={styles.cancelButton} onClick={onClose}>
            Cancelar
          </button>
          <button className={styles.applyButton} onClick={handleApply}>
            Filtrar
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDom.createPortal(modalContent, document.body);
};

export default FilterConfigModal;

